import { type MouseEvent } from 'react';
import { Box, Text, Grid } from '@simplywallst/ui-core';
import { Badge } from '@simplywallst/ui-core/next';
import PNV from '@/components/PositiveNegativeValue';
import { Snowflake } from '@simplywallst/ui-charts/Snowflake';
import WatchlistAddRemoveCompanyToggle from '@/components/WatchlistAddRemoveCompanyToggle';
import { CompanyLogo } from '../CompanyLogo';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import { useTracking } from 'react-tracking';
import { getCloudflareImageUrl } from '@/hooks/useCloudflareImage';
import InfoDescriptionSolid from '@simplywallst/ui-core/icons/InfoDescriptionSolid';
import { RouterLink } from '@/utilities/redux-first-router.export';
import { Tile } from '@/components/Tile';
import { BackgroundImage, PerformancePill } from './styled';

type SnowflakePoints = [number, number, number, number, number];

export type Props = {
  index: number;
  /**
   * URL to link to when clicking on the tile
   */
  href: string;
  companyId: string;
  companyName: string;
  uniqueSymbol: string;
  description: string;
  website: string;
  marketCap: string;
  return7D: number | null;
  return7DFormatted: string;
  return1Y: number | null;
  return1YFormatted: string;
  tickerSymbol: string;
  sharePrice: string;
  snowflakePoints: SnowflakePoints;
  industry: string;
  peRatio: string;
  growthEstimate: string;
  analystPriceTarget: string;
  isRecentSearchEntry?: boolean | null;
  hasLogo: boolean;
  onMouseOver?: () => void;
  onMouseOut?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
};

export const CompanyTile = ({
  index,
  href,
  companyId,
  companyName,
  uniqueSymbol,
  description,
  website,
  marketCap,
  return7D,
  return7DFormatted,
  return1Y,
  return1YFormatted,
  tickerSymbol,
  sharePrice,
  snowflakePoints,
  industry,
  peRatio,
  growthEstimate,
  analystPriceTarget,
  isRecentSearchEntry,
  onMouseOver,
  onMouseOut,
  onFocus,
  onBlur,
  hasLogo,
}: Props) => {
  const { trackEvent } = useTracking({
    action: 'click',
    subject: 'tile',
    uniqueSymbol: uniqueSymbol,
    companyId: companyId,
    index,
  });

  const { isMobile, isTablet } = useUserAgent();
  const isMobileOrTablet = isMobile || isTablet;

  const validWebsite =
    typeof website === 'string' && website !== '' && website !== 'n/a';

  const domain = website?.replace(/^https?:\/\//, '');

  const companyLogoUrl =
    validWebsite && hasLogo
      ? getCloudflareImageUrl({
          src: `https://images.simplywall.st/asset/logos/${domain}`,
          width: 50,
          blur: 13,
          brightness: 0.55,
          contrast: 2.75,
        })
      : '';

  return (
    <RouterLink
      to={href}
      noUnderline
      onClick={() => trackEvent()}
      onFocus={() => onFocus?.()}
      onBlur={() => onBlur?.()}
    >
      <Tile
        onMouseOver={() => onMouseOver?.()}
        onMouseOut={() => onMouseOut?.()}
      >
        <div className="pointer-events-none absolute inset-0 grid place-items-center overflow-hidden opacity-90 print:hidden">
          <BackgroundImage imageUrl={companyLogoUrl} />
        </div>
        <Tile.Front>
          <Grid
            height="310px"
            padding="x1"
            paddingLeft="x1_5"
            templateColumns="100%"
            templateRows="50px auto 50px"
          >
            <Tile.FadeInAnimation>
              <Box width="85%">
                <Text typography="baseStrong" lineClamp={1}>
                  {companyName}
                </Text>
                <Text typography="xSmall" color="whiteRGBA" opacity={0.7}>
                  <span className="sr-only">Market Cap: </span>
                  {marketCap}
                </Text>
                <Text typography="xSmall" lineClamp={1}>
                  {description}
                </Text>
                {isRecentSearchEntry && (
                  <Grid justifyContent="flex-start" paddingTop="x0_5">
                    <Badge tone="attention">New</Badge>
                  </Grid>
                )}
              </Box>
            </Tile.FadeInAnimation>
            <Tile.ScaleInAnimation>
              <Grid alignItems="center" justifyContent="center">
                <Box width="170px" padding="x1" marginTop="x3">
                  <Snowflake points={snowflakePoints}>
                    <Snowflake.Labels />
                  </Snowflake>
                </Box>
              </Grid>
            </Tile.ScaleInAnimation>
            <Tile.FadeInAnimation>
              <Grid
                templateColumns="0.4fr 0.3fr auto"
                alignItems="flex-end"
                height="100%"
              >
                <div>
                  <Text typography="tiny" color="whiteRGBA" opacity={0.7}>
                    {tickerSymbol}
                  </Text>
                  <PerformancePill>
                    <Text typography="xSmall">{sharePrice}</Text>
                  </PerformancePill>
                </div>
                <div>
                  <Text typography="tiny" color="whiteRGBA" opacity={0.7}>
                    7D
                  </Text>
                  <PerformancePill>
                    <Text typography="xSmall">
                      <PNV value={return7D}>{return7DFormatted}</PNV>
                    </Text>
                  </PerformancePill>
                </div>
                <div>
                  <Text typography="tiny" color="whiteRGBA" opacity={0.7}>
                    1Y
                  </Text>
                  <PerformancePill>
                    <Text typography="xSmall">
                      <PNV value={return1Y}>{return1YFormatted}</PNV>
                    </Text>
                  </PerformancePill>
                </div>
              </Grid>
            </Tile.FadeInAnimation>
          </Grid>
        </Tile.Front>
        <Tile.Back>
          <Tile.FadeInAnimation>
            <Grid
              height="310px"
              padding="x1"
              paddingLeft="x1_5"
              templateColumns="100%"
              templateRows="40px auto 50px"
            >
              <Grid
                templateColumns="74px minmax(0, 1fr) 40px"
                alignItems="center"
              >
                <CompanyLogo
                  website={website}
                  tickerSymbol={tickerSymbol}
                  size={24}
                  hasLogo={hasLogo}
                />
                <Box marginRight="x0_5" className="text-right">
                  <Badge tone="default" className="max-w-full">
                    {industry}
                  </Badge>
                </Box>
              </Grid>
              <Tile.FadeInAnimation>
                <Box marginTop="x1">
                  <Text typography="xSmall" lineClamp={8}>
                    {description}
                  </Text>
                </Box>
              </Tile.FadeInAnimation>
              <Grid
                templateColumns="0.35fr 0.35fr auto"
                alignItems="flex-end"
                height="100%"
              >
                <div>
                  <Text typography="tiny" color="whiteRGBA" opacity={0.7}>
                    P/E
                  </Text>
                  <Text typography="xSmall">{peRatio}</Text>
                </div>
                <div>
                  <Text typography="tiny" color="whiteRGBA" opacity={0.7}>
                    Growth
                  </Text>
                  <Text typography="xSmall">{growthEstimate}</Text>
                </div>
                <div>
                  <Text typography="tiny" color="whiteRGBA" opacity={0.7}>
                    Analyst Target
                  </Text>
                  <Text typography="xSmall">{analystPriceTarget}</Text>
                </div>
              </Grid>
            </Grid>
          </Tile.FadeInAnimation>
        </Tile.Back>
        <Box position="absolute" top={10} right={10} width={35}>
          <WatchlistAddRemoveCompanyToggle
            companyId={companyId}
            tickerSymbol={tickerSymbol}
            styleType="gentle"
            minimal
            onClick={(e: MouseEvent) => {
              e.preventDefault();
            }}
          />
        </Box>
        {isMobileOrTablet && (
          <Box position="absolute" bottom={10} right={10}>
            <Grid
              borderRadius="x5"
              width="40px"
              height="40px"
              boxShadow="subtleBorder"
              backgroundColor="transparentSubtle"
            >
              <Tile.SideToggleButton>
                <InfoDescriptionSolid />
              </Tile.SideToggleButton>
            </Grid>
          </Box>
        )}
      </Tile>
    </RouterLink>
  );
};
